import { forwardRef } from "react";
import { Tooltip, Badge } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import type { SubscriptionV2Variable } from "@/pages/subscriptions/services/types";
import { VariableType } from "@/pages/subscriptions/constants";

interface VariablePillProps {
  variable: SubscriptionV2Variable;
  onClick?: () => void;
  categoryDisplayNameLookup?: Record<string, string>;
  metricDisplayNameLookup?: Record<string, string>;
  unit?: string;
}

const VariablePill = forwardRef<HTMLButtonElement, VariablePillProps>(
  (
    {
      variable,
      onClick,
      categoryDisplayNameLookup = {},
      metricDisplayNameLookup = {},
      unit = "",
      ...props
    },
    ref
  ) => {
    const isEditable = variable.config?.isEditable !== false;
    const fromPreviousStep = variable.fromPreviousStep === true;

    // Get display value based on variable type
    const getDisplayValue = () => {
      // For entity type variables, use the category lookup
      if (
        variable.type === VariableType.ENTITY &&
        variable.value &&
        categoryDisplayNameLookup[variable.value]
      ) {
        return categoryDisplayNameLookup[variable.value];
      }

      // For metric type variables, use the metric lookup
      if (
        variable.type === VariableType.METRIC &&
        variable.value &&
        metricDisplayNameLookup[variable.value]
      ) {
        return metricDisplayNameLookup[variable.value];
      }

      // For threshold type variables, append unit (% or metric unit)
      if (variable.type === VariableType.THRESHOLD) {
        // Check if displayValue already has "%" (from is_percentage backend field)
        if (variable.displayValue.includes("%")) {
          return variable.displayValue;
        }

        // Check if displayValue already has the unit
        if (unit && variable.displayValue.includes(unit)) {
          return variable.displayValue;
        }

        // Otherwise, add the metric unit
        if (unit) {
          return `${variable.displayValue} ${unit}`;
        }

        return variable.displayValue;
      }

      // Fall back to displayValue
      return variable.displayValue;
    };

    const displayValue = getDisplayValue();

    // Determine pill style based on state
    const getPillClassName = () => {
      return "al-border-purple-600 al-bg-purple-50 al-text-purple-700 hover:al-bg-purple-100 hover:al-border-purple-700 al-cursor-pointer";
    };

    const pill = (
      <button
        ref={ref}
        onClick={onClick}
        disabled={!isEditable}
        className={cn(
          "al-inline-flex al-items-center al-gap-1.5 al-px-3 al-py-1 al-rounded-full al-border-2 al-text-sm al-font-medium al-transition-all",
          getPillClassName()
        )}
        {...props}
      >
        <span>{displayValue}</span>
        {variable.filterCount && variable.filterCount > 0 && (
          <Badge
            variant="outline"
            className="al-text-xs al-px-1.5 al-py-0 al-bg-purple-700 al-text-white"
          >
            {variable.filterCount} filter{variable.filterCount > 1 ? "s" : ""}
          </Badge>
        )}
      </button>
    );

    // Wrap with tooltip if from previous step
    if (fromPreviousStep && variable.tooltip) {
      return (
        <Tooltip
          title={<div className="al-max-w-xs al-whitespace-pre-line">{variable.tooltip}</div>}
        >
          {pill}
        </Tooltip>
      );
    }

    return pill;
  }
);

VariablePill.displayName = "VariablePill";

export default VariablePill;
